import { useQuery } from '@tanstack/react-query';

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

function proxied(url: string): string {
  return `${CORS_PROXY}${encodeURIComponent(url)}`;
}

/** Ordered list of price sources; the first to return a positive number wins. */
const PRICE_SOURCES: Array<(signal: AbortSignal) => Promise<number | null>> = [
  // CoinGecko
  async (signal) => {
    const res = await fetch(
      proxied('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin-cash&vs_currencies=usd'),
      { signal },
    );
    if (!res.ok) return null;
    const data = await res.json();
    const p = data?.['bitcoin-cash']?.usd;
    return typeof p === 'number' && p > 0 ? p : null;
  },
  // Coinbase
  async (signal) => {
    const res = await fetch(proxied('https://api.coinbase.com/v2/prices/BCH-USD/spot'), { signal });
    if (!res.ok) return null;
    const data = await res.json();
    const p = parseFloat(data?.data?.amount);
    return Number.isFinite(p) && p > 0 ? p : null;
  },
  // Kraken
  async (signal) => {
    const res = await fetch(proxied('https://api.kraken.com/0/public/Ticker?pair=BCHUSD'), { signal });
    if (!res.ok) return null;
    const data = await res.json();
    const result = data?.result;
    const key = result && Object.keys(result)[0];
    const p = key ? parseFloat(result[key]?.c?.[0]) : NaN;
    return Number.isFinite(p) && p > 0 ? p : null;
  },
];

async function fetchBchPrice(signal: AbortSignal): Promise<number> {
  for (const source of PRICE_SOURCES) {
    try {
      const price = await source(signal);
      if (price && price > 0) return price;
    } catch {
      // try the next source
    }
  }
  throw new Error('Could not fetch BCH price from any source');
}

/**
 * Fetch the current BCH/USD price. Cached for 60s and refreshed periodically
 * so the verification fee quote stays reasonably current.
 */
export function useBCHPrice() {
  return useQuery({
    queryKey: ['bch-price'],
    queryFn: ({ signal }) => fetchBchPrice(signal),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    retry: 2,
  });
}
